import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { AuthResponse } from '@doable/shared-types'

/**
 * Decode the JWT payload client-side to read non-sensitive UI hints
 * (platformAdmin, demo) without a separate /me round-trip. We trust
 * our own server's signature here for UI purposes only — the actual
 * admin endpoints re-check the env allowlist on every request, so
 * this is not a security boundary.
 *
 * Returns null on any decode failure (malformed token, server bug).
 * Callers fall back to "treat as non-admin" which is the safe default.
 */
function decodeJwtPayload(token: string): {
  platformAdmin?: boolean
  demo?: boolean
} | null {
  try {
    const middle = token.split('.')[1]
    if (!middle) return null
    // base64url → base64
    const b64 = middle.replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

/**
 * Language mode controls how much technical detail bleeds through into
 * the chat UI:
 *   moderate   — default. End-user friendly headlines only ("Working on
 *                your changes"). Step events stay visible (they're
 *                already human-shaped). Log events with file paths /
 *                tool names are hidden from the headline; advanced users
 *                can still expand "show details" to see them.
 *   technical  — opt-in. Same chat UX as before W3 — headline is the
 *                most recent log/step line, raw and detailed.
 */
export type LanguageMode = 'moderate' | 'technical'

type AuthState = {
  user: AuthResponse['user'] | null
  tenant: AuthResponse['tenant'] | null
  accessToken: string | null
  refreshToken: string | null
  /** True when the current session came from /auth/demo. Hard-expires;
   *  no refresh. Web layer reads this to render the demo banner. */
  isDemo: boolean
  /** True when the JWT carries platformAdmin: true (env allowlist).
   *  UI hint only — server re-checks on every /admin/* request. */
  isPlatformAdmin: boolean
  languageMode: LanguageMode
  setSession: (auth: AuthResponse) => void
  setLanguageMode: (mode: LanguageMode) => void
  clear: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenant: null,
      accessToken: null,
      refreshToken: null,
      isDemo: false,
      isPlatformAdmin: false,
      languageMode: 'moderate',
      setSession: (auth) => {
        const payload = decodeJwtPayload(auth.accessToken)
        set({
          user: auth.user,
          tenant: auth.tenant,
          accessToken: auth.accessToken,
          refreshToken: auth.refreshToken,
          isDemo: auth.demo === true || payload?.demo === true,
          isPlatformAdmin: payload?.platformAdmin === true,
        })
      },
      setLanguageMode: (mode) => set({ languageMode: mode }),
      // Note: clear() preserves languageMode — that's a UI preference,
      // not session state. Logging out shouldn't reset it.
      clear: () =>
        set({
          user: null,
          tenant: null,
          accessToken: null,
          refreshToken: null,
          isDemo: false,
          isPlatformAdmin: false,
        }),
    }),
    {
      name: 'doable-auth',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)
